import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { servicesAPI, productsAPI, categoriesAPI, favoritesAPI, locationsAPI, providersAPI, recommendationsAPI } from '../api';
import { useAuth } from '../hooks/useAuth';
import AdCard from '../components/AdCard';
import AdBanner from '../components/AdBanner';
import SidePanel from '../components/SidePanel';
import SEO from '../components/SEO';

const CATEGORY_ICONS = {
  'home-services': '🏠', 'beauty-wellness': '💅', 'technology': '💻', 'education': '📚',
  'transportation': '🚚', 'events': '🎉', 'health': '❤️', 'construction': '🔨',
  'agriculture': '🌱', 'fashion': '✂️', 'electronics': '📱', 'food-beverages': '☕',
  'clothing': '👕', 'home-garden': '🏡', 'legal-finance': '⚖️', 'automotive': '🚗',
  'cleaning-services': '🧹', 'security': '🛡️', 'real-estate': '🏢', 'media-creative': '🎨',
  'fitness-sports': '💪', 'pet-services': '🐾', 'travel-tourism': '✈️', 'consulting': '💼',
  'books-stationery': '📖', 'baby-kids': '👶', 'arts-crafts': '🎭', 'auto-parts': '🔧',
  'health-beauty-products': '💄', 'sports-outdoor': '⚽',
  'banking-finance': '🏦', 'media-broadcasting': '📺',
};

/* ---- Fade-in-on-scroll hook ---- */
const useFadeIn = () => {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) { el.classList.add('visible'); obs.unobserve(el); }
    }, { threshold: 0.05, rootMargin: '50px' });
    obs.observe(el);
    // Immediately check if already in viewport (race condition fix)
    const rect = el.getBoundingClientRect();
    if (rect.top < window.innerHeight + 50 && rect.bottom > 0) {
      el.classList.add('visible');
      obs.disconnect();
    }
    return () => obs.disconnect();
  }, []);
  return ref;
};

const FadeSection = ({ children, className = '' }) => {
  const ref = useFadeIn();
  return <div ref={ref} className={`fade-section ${className}`}>{children}</div>;
};

const HomePage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [allAds, setAllAds] = useState([]);
  const [categories, setCategories] = useState([]);
  const [provinces, setProvinces] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [sectors, setSectors] = useState([]);
  const [providers, setProviders] = useState([]);
  const [favIds, setFavIds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [forYouItems, setForYouItems] = useState([]);
  const [forYouLoaded, setForYouLoaded] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [search, setSearch] = useState('');
  const [searchLocation, setSearchLocation] = useState('');
  const [searchDistrict, setSearchDistrict] = useState('');
  const [searchSector, setSearchSector] = useState('');
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [showAllCategories, setShowAllCategories] = useState(false);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [srvPage, setSrvPage] = useState(1);
  const [prodPage, setProdPage] = useState(1);
  const [hasMoreSrv, setHasMoreSrv] = useState(true);
  const [hasMoreProd, setHasMoreProd] = useState(true);
  const catScrollRef = useRef(null);
  const sentinelRef = useRef(null);

  const PAGE_SIZE = 12;

  useEffect(() => {
    const fetches = [
      servicesAPI.getAll({ limit: PAGE_SIZE, page: 1 }).catch(e => { console.error('Services fetch failed:', e); return { data: { services: [], pagination: {} } }; }),
      productsAPI.getAll({ limit: PAGE_SIZE, page: 1 }).catch(e => { console.error('Products fetch failed:', e); return { data: { products: [], pagination: {} } }; }),
      categoriesAPI.getAll().catch(e => { console.error('Categories fetch failed:', e); return { data: { tree: [] } }; }),
      providersAPI.search({ limit: 6 }).catch(() => ({ data: { providers: [] } })),
    ];
    if (user) fetches.push(favoritesAPI.getIds().catch(() => ({ data: { favorites: [] } })));

    Promise.all(fetches).then(([servRes, prodRes, catRes, provRes, favRes]) => {
      const services = (servRes.data.services || []).map(s => ({ ...s, _type: 'service' }));
      const products = (prodRes.data.products || []).map(p => ({ ...p, _type: 'product' }));
      const mixed = [...services, ...products].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      setAllAds(mixed);
      setCategories(catRes.data.tree || []);
      setProviders(provRes.data.providers || []);
      if (favRes) setFavIds(favRes.data.favorites || []);
      const srvPag = servRes.data.pagination || {};
      const prodPag = prodRes.data.pagination || {};
      setHasMoreSrv((srvPag.page || 1) < (srvPag.pages || 1));
      setHasMoreProd((prodPag.page || 1) < (prodPag.pages || 1));
    }).catch(err => { console.error('HomePage fetch error:', err); })
      .finally(() => { setLoading(false); });
    // Fetch personalized recommendations
    if (user) {
      recommendationsAPI.getForYou({ limit: 12 }).then(res => {
        if (res.data.personalized) {
          const mixed = [
            ...(res.data.services || []).map(s => ({ ...s, _type: 'service' })),
            ...(res.data.products || []).map(p => ({ ...p, _type: 'product' })),
          ];
          setForYouItems(mixed);
        }
        setForYouLoaded(true);
      }).catch(() => setForYouLoaded(true));
    }
    locationsAPI.getAll().then(res => { setProvinces(res.data.provinces || []); setDistricts(res.data.districts || []); setSectors(res.data.sectors || []); }).catch(console.error);
  }, [user]);

  /* ---- Infinite scroll: load more ---- */
  const loadMore = useCallback(async () => {
    if (loading || loadingMore || (!hasMoreSrv && !hasMoreProd)) return;
    setLoadingMore(true);
    try {
      const fetches = [];
      const nextSrvPage = srvPage + 1;
      const nextProdPage = prodPage + 1;
      if (hasMoreSrv) fetches.push(servicesAPI.getAll({ limit: PAGE_SIZE, page: nextSrvPage }));
      else fetches.push(null);
      if (hasMoreProd) fetches.push(productsAPI.getAll({ limit: PAGE_SIZE, page: nextProdPage }));
      else fetches.push(null);

      const [srvRes, prodRes] = await Promise.all(fetches);
      let newItems = [];
      if (srvRes) {
        const services = (srvRes.data.services || []).map(s => ({ ...s, _type: 'service' }));
        newItems = [...newItems, ...services];
        const pag = srvRes.data.pagination || {};
        setHasMoreSrv((pag.page || nextSrvPage) < (pag.pages || 1));
        setSrvPage(nextSrvPage);
      }
      if (prodRes) {
        const products = (prodRes.data.products || []).map(p => ({ ...p, _type: 'product' }));
        newItems = [...newItems, ...products];
        const pag = prodRes.data.pagination || {};
        setHasMoreProd((pag.page || nextProdPage) < (pag.pages || 1));
        setProdPage(nextProdPage);
      }
      if (newItems.length > 0) {
        newItems.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
        setAllAds(prev => {
          const existingKeys = new Set(prev.map(a => `${a._type}-${a.id}`));
          const unique = newItems.filter(a => !existingKeys.has(`${a._type}-${a.id}`));
          return [...prev, ...unique];
        });
      }
    } catch (err) { console.error(err); }
    finally { setLoadingMore(false); }
  }, [loading, loadingMore, hasMoreSrv, hasMoreProd, srvPage, prodPage]);

  /* ---- Sentinel observer for infinite scroll ---- */
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) loadMore();
    }, { rootMargin: '400px' });
    obs.observe(el);
    return () => obs.disconnect();
  }, [loadMore]);

  /* ---- Category scroll helpers ---- */
  const checkScroll = useCallback(() => {
    const el = catScrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 4);
    setCanScrollRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 4);
  }, []);

  useEffect(() => {
    const el = catScrollRef.current;
    if (!el) return;
    checkScroll();
    el.addEventListener('scroll', checkScroll, { passive: true });
    window.addEventListener('resize', checkScroll);
    return () => { el.removeEventListener('scroll', checkScroll); window.removeEventListener('resize', checkScroll); };
  }, [categories, checkScroll]);

  const scrollCats = (dir) => {
    const el = catScrollRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * 260, behavior: 'smooth' });
  };

  /* ---- Filtered ads based on selected category (includes children) ---- */
  const getCategoryIds = (catId) => {
    const cat = categories.find(c => String(c.id) === String(catId));
    if (!cat) return [String(catId)];
    const ids = [String(cat.id)];
    (cat.children || []).forEach(ch => {
      ids.push(String(ch.id));
      (ch.children || []).forEach(sub => ids.push(String(sub.id)));
    });
    return ids;
  };
  const filteredAds = selectedCategory
    ? (() => { const ids = getCategoryIds(selectedCategory); return allAds.filter(ad => ids.includes(String(ad.category_id))); })()
    : allAds;

  const isFav = (type, id) => favIds.some(f => f.item_type === type && f.item_id === id);
  const handleToggleFav = (type, id, action) => {
    if (action === 'added') setFavIds(prev => [...prev, { item_type: type, item_id: id }]);
    else setFavIds(prev => prev.filter(f => !(f.item_type === type && f.item_id === id)));
  };

  const filteredSearchDistricts = searchLocation ? districts.filter(d => String(d.province_id) === String(searchLocation)) : [];
  const filteredSearchSectors = searchDistrict ? sectors.filter(s => String(s.district_id) === String(searchDistrict)) : [];

  const handleSearch = (e) => {
    e.preventDefault();
    const params = new URLSearchParams();
    if (search.trim()) params.set('search', search.trim());
    if (searchLocation) params.set('province_id', searchLocation);
    if (searchDistrict) params.set('district_id', searchDistrict);
    if (searchSector) params.set('sector_id', searchSector);
    navigate(`/services?${params.toString()}`);
  };

  const handleCategoryClick = (catId) => {
    setSelectedCategory(catId === selectedCategory ? null : catId);
    setShowAllCategories(false);
  };

  const selectedCatName = selectedCategory
    ? categories.find(c => String(c.id) === String(selectedCategory))?.name
    : null;

  return (
    <div className="min-h-screen bg-gray-100">
      <SEO title="Find Services & Products in Rwanda" description="TopInfo Rwanda - Rwanda's #1 online marketplace. Find trusted service providers, buy & sell products, hire professionals, and discover great deals in Kigali and across all provinces. Home services, beauty, technology, construction, education & more." keywords="Rwanda marketplace, buy sell Rwanda, services Kigali, products Rwanda, TopInfo Rwanda, hire professionals Kigali, book services Rwanda, online marketplace, classified ads Rwanda, service providers, buy products online Rwanda, home services Kigali, beauty services, technology services, construction Rwanda, cleaning Kigali, transportation, education, electrician plumber mechanic Kigali, freelancer Rwanda, Gasabo Kicukiro Nyarugenge, East Africa marketplace" jsonLd={{
            '@context': 'https://schema.org',
            '@type': 'WebPage',
            name: 'TopInfo Rwanda - Find Services & Products',
            description: "Rwanda's #1 marketplace. Find trusted service providers, buy & sell products.",
            url: 'https://topinfo.rw',
          }} />

      {/* ========== HERO SECTION ========== */}
      <section className="bg-primary-600 relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.06]" style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)', backgroundSize: '32px 32px' }} />
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 pt-10 pb-12 md:pt-14 md:pb-16 text-center">
          {/* Headline */}
          <h1 className="font-heading text-3xl sm:text-4xl md:text-5xl font-extrabold text-white leading-tight mb-4">
            Find Services & Buy & Sell<br className="hidden sm:block" /> Anything Locally
          </h1>
          <p className="text-white/70 text-sm md:text-base mb-8 max-w-xl mx-auto">
            Rwanda's largest classified marketplace — connect with sellers, hire service providers, and discover great deals near you.
          </p>

          {/* Search Bar — Desktop */}
          <form onSubmit={handleSearch}>
            <div className="max-w-2xl mx-auto hidden sm:flex bg-white rounded-xl shadow-xl overflow-hidden">
              <div className="flex-1 flex items-center gap-3 px-4">
                <svg className="w-5 h-5 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
                <input type="text" value={search} onChange={(e) => setSearch(e.target.value)}
                  placeholder="What are you looking for?"
                  className="w-full py-4 text-base outline-none placeholder-gray-400 text-gray-900" />
              </div>
              <div className="flex items-center border-l border-gray-100">
                <div className="flex items-center gap-2 px-3">
                  <svg className="w-4 h-4 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" /></svg>
                  <select value={searchLocation} onChange={(e) => setSearchLocation(e.target.value)}
                    className="py-4 text-sm text-gray-600 outline-none bg-transparent min-w-[120px]">
                    <option value="">All Rwanda</option>
                    {provinces.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </select>
                </div>
                <button type="submit" className="bg-primary-600 hover:bg-primary-700 text-white font-bold px-8 py-4 text-base transition-colors flex items-center gap-2">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
                  Search
                </button>
              </div>
            </div>

            {/* Search Bar — Mobile: stacked for proper fit */}
            <div className="max-w-lg mx-auto sm:hidden space-y-2.5">
              <div className="flex items-center bg-white rounded-xl shadow-xl px-3.5 gap-2.5">
                <svg className="w-5 h-5 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
                <input type="text" value={search} onChange={(e) => setSearch(e.target.value)}
                  placeholder="What are you looking for?"
                  className="flex-1 py-3.5 text-sm outline-none placeholder-gray-400 text-gray-900 min-w-0" />
              </div>
              <div className="flex gap-2">
                <div className="flex-1 flex items-center bg-white rounded-xl shadow-md px-3 gap-2 min-w-0">
                  <svg className="w-4 h-4 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" /></svg>
                  <select value={searchLocation} onChange={(e) => { setSearchLocation(e.target.value); setSearchDistrict(''); setSearchSector(''); }}
                    className="flex-1 py-3 text-sm text-gray-600 outline-none bg-transparent min-w-0">
                    <option value="">All Rwanda</option>
                    {provinces.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </select>
                </div>
                <button type="submit" className="bg-white hover:bg-gray-50 text-primary-600 font-bold px-5 py-3 rounded-xl shadow-md text-sm transition-colors flex items-center gap-1.5 flex-shrink-0">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
                  Search
                </button>
              </div>
              {filteredSearchDistricts.length > 0 && (
                <div className="flex gap-2">
                  <div className="flex-1 flex items-center bg-white rounded-xl shadow-md px-3 gap-2 min-w-0">
                    <select value={searchDistrict} onChange={(e) => { setSearchDistrict(e.target.value); setSearchSector(''); }}
                      className="flex-1 py-3 text-sm text-gray-600 outline-none bg-transparent min-w-0">
                      <option value="">All districts</option>
                      {filteredSearchDistricts.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                    </select>
                  </div>
                  {filteredSearchSectors.length > 0 && (
                    <div className="flex-1 flex items-center bg-white rounded-xl shadow-md px-3 gap-2 min-w-0">
                      <select value={searchSector} onChange={(e) => setSearchSector(e.target.value)}
                        className="flex-1 py-3 text-sm text-gray-600 outline-none bg-transparent min-w-0">
                        <option value="">All sectors</option>
                        {filteredSearchSectors.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                      </select>
                    </div>
                  )}
                </div>
              )}
            </div>
          </form>
        </div>
      </section>

      {/* ========== CATEGORY TABS ========== */}
      {/* Ad: Top Banner (scrolls with page) */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-4 pb-2">
        <AdBanner placement="homepage-top" maxAds={5} interval={5000} />
      </div>

      <section className="bg-white border-b border-gray-200 sticky top-14 z-30 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">

          <div className="relative flex items-center">
            {/* Left scroll arrow — desktop only */}
            {canScrollLeft && (
              <button onClick={() => scrollCats(-1)}
                className="hidden sm:flex absolute left-0 z-10 w-8 h-8 items-center justify-center bg-white/95 rounded-full shadow-md border border-gray-200 hover:bg-gray-50 transition-colors -ml-1"
                aria-label="Scroll categories left">
                <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M15 19l-7-7 7-7" /></svg>
              </button>
            )}

            <div ref={catScrollRef}
              className="flex gap-1 overflow-x-auto py-2.5 scrollbar-hide flex-1"
              style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
              {/* "All" tab */}
              <button onClick={() => { setSelectedCategory(null); setShowAllCategories(false); }}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-all duration-200 flex-shrink-0 border ${
                  !selectedCategory
                    ? 'bg-primary-600 text-white border-primary-600 shadow-sm'
                    : 'bg-gray-50 text-gray-600 border-transparent hover:bg-primary-50 hover:text-primary-600 hover:border-primary-200'
                }`}>
                <span className="text-base">🏪</span>
                <span>All</span>
              </button>

              {categories.slice(0, 12).map(cat => (
                <button key={cat.id} onClick={() => handleCategoryClick(cat.id)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-all duration-200 flex-shrink-0 border ${
                    String(selectedCategory) === String(cat.id)
                      ? 'bg-primary-600 text-white border-primary-600 shadow-sm'
                      : 'bg-gray-50 text-gray-600 border-transparent hover:bg-primary-50 hover:text-primary-600 hover:border-primary-200'
                  }`}>
                  <span className="text-base">{CATEGORY_ICONS[cat.slug] || '📦'}</span>
                  <span>{cat.name}</span>
                </button>
              ))}

              {/* View All Categories toggle */}
              <button onClick={() => setShowAllCategories(v => !v)}
                className={`flex items-center gap-1 px-4 py-2 rounded-lg text-sm font-semibold whitespace-nowrap transition-all flex-shrink-0 ${
                  showAllCategories ? 'bg-primary-50 text-primary-700' : 'text-primary-600 hover:bg-primary-50'
                }`}>
                {showAllCategories ? 'Hide' : 'View All'} Categories
                <svg className={`w-3.5 h-3.5 transition-transform duration-200 ${showAllCategories ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 9l-7 7-7-7" /></svg>
              </button>
            </div>

            {/* Right scroll arrow — desktop only */}
            {canScrollRight && (
              <button onClick={() => scrollCats(1)}
                className="hidden sm:flex absolute right-0 z-10 w-8 h-8 items-center justify-center bg-white/95 rounded-full shadow-md border border-gray-200 hover:bg-gray-50 transition-colors -mr-1"
                aria-label="Scroll categories right">
                <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" /></svg>
              </button>
            )}
          </div>
        </div>

        {/* All Categories Grid — expandable */}
        {showAllCategories && (
          <div className="border-t border-gray-100 bg-gray-50/80">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4">
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2">
                {categories.map(cat => (
                  <button key={cat.id} onClick={() => handleCategoryClick(cat.id)}
                    className={`flex items-center gap-2.5 px-3.5 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 text-left ${
                      String(selectedCategory) === String(cat.id)
                        ? 'bg-primary-600 text-white shadow-sm'
                        : 'bg-white text-gray-700 border border-gray-100 hover:border-primary-200 hover:bg-primary-50 hover:text-primary-600 shadow-sm'
                    }`}>
                    <span className="text-lg">{CATEGORY_ICONS[cat.slug] || '📦'}</span>
                    <span className="truncate">{cat.name}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
      </section>

      {/* ========== LISTINGS + SIDEBAR ========== */}
      <FadeSection>
        <section className="w-full px-2 sm:px-4 py-8 md:py-10">
          <div className="flex justify-center gap-4">
            {/* Left Side Panel — Desktop only */}
            <SidePanel />

            {/* Center Content */}
            <div className="max-w-7xl w-full flex flex-col lg:flex-row gap-6">
            {/* Main content — Listings */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between mb-5">
                <div>
                  <h2 className="font-heading text-xl md:text-2xl font-bold text-gray-900">
                    {selectedCatName || 'Fresh Recommendations'}
                  </h2>
                  {selectedCategory && (
                    <p className="text-sm text-gray-500 mt-0.5">
                      {filteredAds.length} listing{filteredAds.length !== 1 ? 's' : ''} found
                      <button onClick={() => setSelectedCategory(null)} className="ml-2 text-primary-600 hover:text-primary-700 font-medium">
                        Clear filter ×
                      </button>
                    </p>
                  )}
                </div>
              </div>
              {loading ? (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 md:gap-4">
                  {[...Array(6)].map((_, i) => (
                    <div key={i} className="bg-white rounded-xl overflow-hidden animate-pulse shadow-sm">
                      <div className="aspect-[4/3] bg-gray-100" />
                      <div className="p-3 space-y-2">
                        <div className="h-4 bg-gray-100 rounded w-2/3" />
                        <div className="h-3 bg-gray-100 rounded w-full" />
                        <div className="h-3 bg-gray-100 rounded w-1/2" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : filteredAds.length === 0 ? (
                <div className="text-center py-16 bg-white rounded-2xl border border-gray-100">
                  <div className="text-4xl mb-3">📦</div>
                  <h3 className="text-gray-900 font-bold mb-1">No listings in this category</h3>
                  <p className="text-gray-500 text-sm mb-4">Try selecting a different category or browse all listings.</p>
                  <button onClick={() => setSelectedCategory(null)} className="text-primary-600 font-semibold text-sm hover:text-primary-700">
                    Show all listings
                  </button>
                </div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 md:gap-4">
                  {filteredAds.map(item => (
                    <AdCard key={`${item._type}-${item.id}`} item={item} type={item._type}
                      isFav={isFav(item._type, item.id)} onToggleFav={handleToggleFav} />
                  ))}
                </div>
              )}
              {/* Infinite scroll sentinel */}
              {(hasMoreSrv || hasMoreProd) && !selectedCategory && (
                <div ref={sentinelRef} className="flex justify-center py-8">
                  {loadingMore && (
                    <div className="flex items-center gap-2 text-gray-400 text-sm">
                      <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>
                      Loading more...
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Sidebar — Top Service Providers */}
            <div className="w-full lg:w-80 flex-shrink-0">
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 lg:sticky lg:top-28">
                <div className="flex items-center justify-between px-5 pt-5 pb-3">
                  <h3 className="font-heading font-bold text-gray-900">Top Service Providers</h3>
                  <Link to="/providers" className="text-primary-600 text-xs font-semibold hover:text-primary-700 transition-colors">View all</Link>
                </div>
                <div className="px-3 pb-4 flex flex-col gap-1">
                  {providers.length > 0 ? providers.slice(0, 5).map(p => (
                    <Link key={p.id} to={`/providers/${p.id}`}
                      className="flex items-center gap-3 px-3 py-3 rounded-lg hover:bg-gray-50 transition-colors group">
                      <div className="w-10 h-10 rounded-full bg-primary-600 flex items-center justify-center text-white font-bold text-xs flex-shrink-0 shadow-sm">
                        {p.first_name?.[0]}{p.last_name?.[0]}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-sm text-gray-900 truncate group-hover:text-primary-600 transition-colors">{p.business_name || `${p.first_name} ${p.last_name}`}</p>
                        <p className="text-xs text-gray-400 truncate mt-0.5">{p.service_description || (p.location ? `📍 ${p.location}` : 'Service Provider')}</p>
                      </div>
                      <svg className="w-4 h-4 text-gray-300 group-hover:text-primary-500 flex-shrink-0 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                    </Link>
                  )) : (
                    [...Array(5)].map((_, i) => (
                      <div key={i} className="flex items-center gap-3 px-3 py-3 animate-pulse">
                        <div className="w-10 h-10 rounded-full bg-gray-100 flex-shrink-0" />
                        <div className="flex-1 space-y-1.5">
                          <div className="h-3.5 bg-gray-100 rounded w-3/4" />
                          <div className="h-3 bg-gray-100 rounded w-1/2" />
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          </div>


          </div>
        </section>
      </FadeSection>

      {/* ========== SAFETY TIPS ========== */}
      {/* Ad: Homepage Mid-Section */}
      <FadeSection>
        <div className="max-w-5xl mx-auto px-4 py-6">
          <AdBanner placement="homepage-mid" maxAds={5} interval={6000} className="mx-auto" />
        </div>
      </FadeSection>

      <FadeSection>
        <section className="bg-white border-t border-gray-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10 md:py-14">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-gray-900 text-center mb-8">Stay Safe on TopInfo</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {[
                { icon: '🛡️', title: 'Meet in Safe Places', desc: 'Pick a busy, well-lit public area when meeting a buyer or seller face to face.' },
                { icon: '💰', title: 'Verify Before Paying', desc: "Never transfer funds or pay upfront until you've confirmed the item or service in person." },
                { icon: '🔍', title: 'Check Everything', desc: 'Examine the product carefully or confirm service details before you hand over any payment.' },
              ].map((tip, i) => (
                <div key={i} className="bg-gray-50 rounded-xl p-5 text-center border border-gray-100 hover:bg-primary-50 hover:border-primary-100 transition-colors duration-300">
                  <span className="text-3xl block mb-2">{tip.icon}</span>
                  <h3 className="font-bold text-gray-900 text-sm mb-1">{tip.title}</h3>
                  <p className="text-gray-500 text-xs leading-relaxed">{tip.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      </FadeSection>
    </div>
  );
};

export default HomePage;
